import {
  AfterViewInit,
  Component,
  ElementRef,
  OnInit,
  ViewChild,
  NgZone,
  OnDestroy,
} from '@angular/core';
import { Observable } from 'rxjs';
import { Pos } from 'src/app/core/classes/pos';
import { PosService } from 'src/app/core/services/pos.service';

import MarkerClusterer from '@googlemaps/markerclustererplus';
import { Router, ActivatedRoute } from '@angular/router';
import { GoogleMapsLoaderService } from 'src/app/core/services/google-maps.service';

@Component({
  selector: 'dq-map',
  templateUrl: './map.component.html',
  styleUrls: ['./map.component.scss'],
})
export class MapComponent implements OnInit, AfterViewInit, OnDestroy {
  pos$!: Observable<Pos[]>;

  constructor(
    private posService: PosService,
    private router: Router,
    private mapLoader: GoogleMapsLoaderService,
    public _ngZone: NgZone,
    public route: ActivatedRoute
  ) {}

  ngOnInit(): void {
    this.pos$ = this.posService.posFiltered$;
    window.my = window.my || {};
    window.my.namespace = window.my.namespace || {};
    window.my.namespace.publicNavigateDetail =
      this.publicNavigateDetail.bind(this);
  }

  ngOnDestroy() {
    window.my.namespace.publicNavigateDetail = null;
  }

  publicNavigateDetail(id: any) {
    this._ngZone.run(() => this.privateNavigateDetail(id));
  }

  privateNavigateDetail(id: any) {
    this.router.navigate(['/detail/' + id], { relativeTo: this.route });
  }

  @ViewChild('mapContainer', { static: false })
  gmap!: ElementRef;

  map!: google.maps.Map;
  markers: google.maps.Marker[] = [];
  cluster: MarkerClusterer | undefined;

  zoomFlag = 'user';
  filterByMap = false;

  mapOptions: google.maps.MapOptions = {
    center: { lat: 0, lng: 0 },
    zoom: 8,
    styles: [
      {
        elementType: 'geometry',
        stylers: [
          {
            color: '#f5f5f5',
          },
        ],
      },
      {
        elementType: 'labels.icon',
        stylers: [
          {
            visibility: 'off',
          },
        ],
      },
      {
        elementType: 'labels.text.fill',
        stylers: [
          {
            color: '#616161',
          },
        ],
      },
      {
        elementType: 'labels.text.stroke',
        stylers: [
          {
            color: '#f5f5f5',
          },
        ],
      },
      {
        featureType: 'administrative.land_parcel',
        elementType: 'labels.text.fill',
        stylers: [
          {
            color: '#bdbdbd',
          },
        ],
      },
      {
        featureType: 'poi',
        elementType: 'geometry',
        stylers: [
          {
            color: '#eeeeee',
          },
        ],
      },
      {
        featureType: 'poi',
        elementType: 'labels.text.fill',
        stylers: [
          {
            color: '#757575',
          },
        ],
      },
      {
        featureType: 'poi.park',
        elementType: 'geometry',
        stylers: [
          {
            color: '#e5e5e5',
          },
        ],
      },
      {
        featureType: 'poi.park',
        elementType: 'labels.text.fill',
        stylers: [
          {
            color: '#9e9e9e',
          },
        ],
      },
      {
        featureType: 'road',
        elementType: 'geometry',
        stylers: [
          {
            color: '#ffffff',
          },
        ],
      },
      {
        featureType: 'road.arterial',
        elementType: 'labels.text.fill',
        stylers: [
          {
            color: '#757575',
          },
        ],
      },
      {
        featureType: 'road.highway',
        elementType: 'geometry',
        stylers: [
          {
            color: '#dadada',
          },
        ],
      },
      {
        featureType: 'road.highway',
        elementType: 'labels.text.fill',
        stylers: [
          {
            color: '#616161',
          },
        ],
      },
      {
        featureType: 'road.local',
        elementType: 'labels.text.fill',
        stylers: [
          {
            color: '#9e9e9e',
          },
        ],
      },
      {
        featureType: 'transit.line',
        elementType: 'geometry',
        stylers: [
          {
            color: '#e5e5e5',
          },
        ],
      },
      {
        featureType: 'transit.station',
        elementType: 'geometry',
        stylers: [
          {
            color: '#eeeeee',
          },
        ],
      },
      {
        featureType: 'water',
        elementType: 'geometry',
        stylers: [
          {
            color: '#c9c9c9',
          },
        ],
      },
      {
        featureType: 'water',
        elementType: 'labels.text.fill',
        stylers: [
          {
            color: '#9e9e9e',
          },
        ],
      },
    ],
  };

  handleEvent = () => {
    if (this.filterByMap && this.zoomFlag === 'user') {
      if (this.map.getBounds()) {
        //console.log('filter');
        this.posService.filterPosLatLon(this.map.getBounds()!);
      }
    }
  };

  async mapInitializer() {
    await this.mapLoader.load();
    this.map = new google.maps.Map(this.gmap.nativeElement, this.mapOptions);

    this.map.addListener('dragend', this.handleEvent);
    this.map.addListener('zoom_changed', this.handleEvent);

    const bounds = new google.maps.LatLngBounds();

    this.pos$.subscribe((pos: Pos[]) => {
      this.zoomFlag = 'api';
      this.deleteMarkers();
      this.deleteClusters();

      const MAX_MARKERS = 500;

      const visiblePos = pos
        .filter((p) => p.lat != undefined && p.lon != undefined)
        .slice(0, MAX_MARKERS);

      visiblePos.forEach((p) => {
        const myLatLng = new google.maps.LatLng(p.lat!, p.lon!);
        const marker = new google.maps.Marker({
          map: this.map,
          position: myLatLng,
          title: p.commercialName,
        });

        const htmlInfoWindow = `<div class='p-3'><a onClick='my.namespace.publicNavigateDetail(${p.id})' href='javascript:;'>${p.commercialName}</a><br/></div>`;
        this.addInfoWindow(marker, htmlInfoWindow);

        bounds.extend(myLatLng);
        this.markers.push(marker);
      });

      this.cluster = new MarkerClusterer(this.map, this.markers, {
        imagePath:
          'https://developers.google.com/maps/documentation/javascript/examples/markerclusterer/m',
      });

      this.map.fitBounds(bounds);
      const zoom = this.map.getZoom();
      this.map.setZoom(zoom && zoom > 18 ? 18 : Number(zoom));
      this.zoomFlag = 'user';
    });
  }

  onButton_click(event: any) {
    alert(event);
  }

  addInfoWindow(marker: google.maps.Marker, message: string) {
    var infoWindow = new google.maps.InfoWindow({
      content: message,
    });

    google.maps.event.addListener(marker, 'click', () => {
      infoWindow.open(this.map, marker);
    });
  }

  deleteMarkers(): void {
    for (let i = 0; i < this.markers.length; i++) {
      this.markers[i].setMap(null);
    }
    this.markers = [];
  }

  deleteClusters(): void {
    if (this.cluster != undefined) {
      this.cluster.clearMarkers();
    }
    this.cluster = undefined;
  }

  ngAfterViewInit(): void {
    this.mapInitializer();
  }

  getBounds() {
    //console.log('oo' + this.map.getBounds());
  }
}
